import React, { useState } from 'react';
import { useQuery, gql } from '@/apollo';
import { GET_APPOINTMENT_DETAILS } from '../../graphql/appointment-details';
import { motion } from 'framer-motion';
import { 
  Stethoscope, 
  Clock, 
  CheckCircle, 
  XCircle,
  Search,
  AlertCircle,
  Info,
  Building,
  ChevronDown
} from 'lucide-react';

const GET_CUSTOMERS = gql`
  query GetCustomers {
    customers {
      id
      name
      description
      isActive
    }
  }
`;

interface Customer {
  id: string;
  name: string;
  description?: string;
}

interface AppointmentDetail {
  id: string;
  name: string;
  canCancel: string;
  canReschedule: string;
  canSchedule: string;
  createdAt: string;
  updatedAt: string;
  customerId?: string;
  customer?: Customer;
  notes: Array<{ id: string; content: string; order: number }>;
}


export const AppointmentDetails: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDetail, setSelectedDetail] = useState<AppointmentDetail | null>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Fetch customers
  const { data: customersData } = useQuery(GET_CUSTOMERS, { fetchPolicy: 'network-only' });
  const customers: Customer[] = customersData?.customers?.filter((c: any) => c.isActive) || [];

  // Fetch appointment details data
  const { data, loading, error } = useQuery(GET_APPOINTMENT_DETAILS, { fetchPolicy: 'network-only' });
  const allDetails: AppointmentDetail[] = data?.appointmentDetails || [];
  
  // Filter details by selected customer
  const details: AppointmentDetail[] = selectedCustomer
    ? allDetails.filter(detail => detail.customerId === selectedCustomer.id)
    : [];
  
  // Suggestions: show full list on focus, filter down as the user types
  const searchSuggestions: AppointmentDetail[] = isSearchFocused && selectedCustomer
    ? details.filter(detail => detail.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : [];

  // Find the selected condition
  const selectedDetailData = selectedDetail || 
    (searchTerm && details.find(detail => detail.name.toLowerCase() === searchTerm.toLowerCase()));


  // Helper function to get cancel status display (icon, text, color)
  const getCancelStatus = (value: string) => {
    // Normalize value: handle legacy "1"/"0", empty strings, and normalize case
    const normalized = (value || 'Yes').toString().trim();
    const lowerValue = normalized.toLowerCase();
    
    if (lowerValue === 'yes' || lowerValue === '1' || lowerValue === 'true') {
      return {
        icon: <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success)' }} />,
        text: 'Can Cancel',
        color: 'var(--color-success)'
      };
    } else if (lowerValue === 'no' || lowerValue === '0' || lowerValue === 'false') {
      return {
        icon: <XCircle className="w-5 h-5" style={{ color: 'var(--color-error)' }} />,
        text: 'Cannot Cancel',
        color: 'var(--color-error)'
      };
    } else { // 'See Notes' (case-insensitive match) or default
      return {
        icon: <AlertCircle className="w-5 h-5" style={{ color: '#f59e0b' }} />,
        text: 'See Notes for Cancelling',
        color: '#f59e0b'
      };
    }
  };

  // Helper function to get reschedule status display (icon, text, color)
  const getRescheduleStatus = (value: string) => {
    // Normalize value: handle legacy "1"/"0", empty strings, and normalize case
    const normalized = (value || 'Yes').toString().trim();
    const lowerValue = normalized.toLowerCase();
    
    if (lowerValue === 'yes' || lowerValue === '1' || lowerValue === 'true') {
      return {
        icon: <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success)' }} />,
        text: 'Can Reschedule',
        color: 'var(--color-success)'
      };
    } else if (lowerValue === 'no' || lowerValue === '0' || lowerValue === 'false') {
      return {
        icon: <XCircle className="w-5 h-5" style={{ color: 'var(--color-error)' }} />,
        text: 'Cannot Reschedule',
        color: 'var(--color-error)'
      };
    } else { // 'See Notes' (case-insensitive match) or default
      return {
        icon: <AlertCircle className="w-5 h-5" style={{ color: '#f59e0b' }} />,
        text: 'See Notes for Rescheduling',
        color: '#f59e0b'
      };
    }
  };

  // Helper function to get schedule status display (icon, text, color)
  const getScheduleStatus = (value: string) => {
    // Normalize value: handle legacy "1"/"0", empty strings, and normalize case
    const normalized = (value || 'No').toString().trim();
    const lowerValue = normalized.toLowerCase();
    
    if (lowerValue === 'yes' || lowerValue === '1' || lowerValue === 'true') {
      return {
        icon: <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success)' }} />,
        text: 'Can Schedule',
        color: 'var(--color-success)'
      };
    } else if (lowerValue === 'no' || lowerValue === '0' || lowerValue === 'false') {
      return {
        icon: <XCircle className="w-5 h-5" style={{ color: 'var(--color-error)' }} />,
        text: 'Cannot Schedule',
        color: 'var(--color-error)'
      };
    } else { // 'See Notes' (case-insensitive match) or default
      return {
        icon: <AlertCircle className="w-5 h-5" style={{ color: '#f59e0b' }} />,
        text: 'See Notes for Scheduling',
        color: '#f59e0b'
      };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--color-accent)' }}></div>
        <p className="ml-3 text-lg" style={{ color: 'var(--color-text-secondary)' }}>Loading appointment details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-error)' }} />
        <p className="text-lg" style={{ color: 'var(--color-error)' }}>Error loading appointment details</p>
        <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Customer Selection */}
      <div 
        className="rounded-xl shadow-sm border p-6"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="relative">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            <Building className="w-4 h-4 inline mr-2" />
            Choose Customer Organization
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
              className="w-full px-4 py-3 border rounded-lg text-left flex items-center justify-between transition-colors"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                color: selectedCustomer ? 'var(--color-text-primary)' : 'var(--color-text-muted)'
              }}
            >
              <span>{selectedCustomer ? selectedCustomer.name : 'Select customer...'}</span>
              <ChevronDown className="w-4 h-4" />
            </button>

            {showCustomerDropdown && (
              <div 
                className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  borderColor: 'var(--color-border)',
                  boxShadow: 'var(--shadow-lg)'
                }}
              >
                {customers.map((customer) => (
                  <button
                    key={customer.id}
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setShowCustomerDropdown(false);
                      setSearchTerm('');
                      setSelectedDetail(null);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-opacity-50 transition-colors border-b last:border-b-0"
                    style={{
                      backgroundColor: selectedCustomer?.id === customer.id ? 'var(--color-bg-secondary)' : 'var(--color-bg-primary)',
                      color: 'var(--color-text-primary)',
                      borderColor: 'var(--color-border)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCustomer?.id !== customer.id) {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                      }
                    }}
                  >
                    <div className="font-medium">{customer.name}</div>
                    {customer.description && (
                      <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {customer.description}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="text-sm mt-2" style={{ color: 'var(--color-text-muted)' }}>
            Select your organization to access appointment details
          </p>
        </div>
      </div>

      {/* Search with Autofill */}
      <div 
        className="rounded-xl shadow-sm border p-6"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)',
          opacity: selectedCustomer ? 1 : 0.6,
          pointerEvents: selectedCustomer ? 'auto' : 'none'
        }}
      >
        <div className="relative">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            <Search className="w-4 h-4 inline mr-2" />
            Search appointment details
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder={selectedCustomer ? "Type to search appointment details..." : "Select a customer first..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setTimeout(() => setIsSearchFocused(false), 150)}
              disabled={!selectedCustomer}
              className="w-full pl-4 pr-4 py-3 border rounded-lg transition-colors"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                cursor: selectedCustomer ? 'text' : 'not-allowed'
              } as React.CSSProperties}
            />
            
            {/* Autofill Suggestions */}
            {isSearchFocused && searchSuggestions.length > 0 && (
              <div 
                className="absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto dark-scrollbar"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  borderColor: 'var(--color-border)',
                  boxShadow: 'var(--shadow-lg)'
                }}
              >
                {searchSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    onClick={() => {
                      setSearchTerm(suggestion.name);
                      setSelectedDetail(suggestion);
                      // Hide autofill menu after selection
                      setTimeout(() => setIsSearchFocused(false), 100);
                    }}
                    className="w-full px-4 py-3 text-left hover:bg-opacity-50 transition-colors border-b last:border-b-0"
                    style={{
                      backgroundColor: 'var(--color-bg-primary)',
                      color: 'var(--color-text-primary)',
                      borderColor: 'var(--color-border)',
                      '--tw-hover-bg-opacity': '0.5'
                    } as React.CSSProperties}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                    }}
                  >
                    <div className="font-medium">{suggestion.name}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Selected Appointment Detail */}
      {selectedDetailData ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-xl shadow-sm border overflow-hidden hover:shadow-lg transition-all duration-300"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          {/* Header */}
          <div 
            className="p-6 border-b"
            style={{ borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h2 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                  {selectedDetailData.name}
                </h2>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Scheduling Policies */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                <Clock className="w-5 h-5 mr-2" />
                Scheduling Policies
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  {getCancelStatus(selectedDetailData.canCancel).icon}
                  <span className="text-sm font-medium" style={{ color: getCancelStatus(selectedDetailData.canCancel).color }}>
                    {getCancelStatus(selectedDetailData.canCancel).text}
                  </span>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  {getRescheduleStatus(selectedDetailData.canReschedule).icon}
                  <span className="text-sm font-medium" style={{ color: getRescheduleStatus(selectedDetailData.canReschedule).color }}>
                    {getRescheduleStatus(selectedDetailData.canReschedule).text}
                  </span>
                </div>
                <div className="flex items-center space-x-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                  {getScheduleStatus(selectedDetailData.canSchedule).icon}
                  <span className="text-sm font-medium" style={{ color: getScheduleStatus(selectedDetailData.canSchedule).color }}>
                    {getScheduleStatus(selectedDetailData.canSchedule).text}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {selectedDetailData.notes && selectedDetailData.notes.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center" style={{ color: 'var(--color-text-primary)' }}>
                  <Info className="w-5 h-5 mr-2" />
                  Additional Notes
                </h3>
                <div className="space-y-2">
                  {[...selectedDetailData.notes]
                    .sort((a: any, b: any) => a.order - b.order)
                    .map((note: any) => (
                      <div 
                        key={note.id}
                        className="p-3 rounded-lg text-sm"
                        style={{ 
                          backgroundColor: 'var(--color-bg-tertiary)',
                          color: 'var(--color-text-secondary)'
                        }}
                      >
                        {note.content}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            
          </div>
        </motion.div>
      ) : null}

      {/* Empty State */}
      {!selectedDetailData && searchTerm && (
        <div 
          className="text-center py-16 rounded-xl border"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)'
          }}
        >
          <Stethoscope className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
          <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            No appointment detail found
          </h3>
          <p className="text-base" style={{ color: 'var(--color-text-secondary)' }}>
            Try searching for a different condition or check the spelling
          </p>
        </div>
      )}

      {/* Initial State (standardized) */}
      {!selectedDetailData && !searchTerm && (
        <div className="text-center py-8">
          <Stethoscope className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
          {!selectedCustomer ? (
            <>
              <p style={{ color: 'var(--color-text-secondary)' }}>Select a customer organization to view appointment details</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>Choose your organization from the dropdown above</p>
            </>
          ) : (
            <>
              <p style={{ color: 'var(--color-text-secondary)' }}>Search appointment details above to view policies.</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>Start typing to find specific appointment rules and notes.</p>
            </>
          )}
        </div>
      )}

      {/* All Appointment Details block removed per request */}
    </div>
  );
};

